import { Suspense, lazy, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Mail, CalendarCheck, ArrowUpRight, Phone, MessageCircle, Quote } from "lucide-react";
import { PhotoFrame } from "@/components/Photo";
import { usePhotos } from "@/hooks/usePhotos";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { FacultyList } from "@/components/FacultyList";
import { VisionMissionCards, TermsSection } from "@/components/HomeSections";
import { ReviewsShowcase, Coverflow } from "@/components/ReviewsShowcase";
import { AnnouncementBar, useAnnouncements } from "@/components/Announcements";
import { CATEGORIES, BRAND, PROGRAM_LIST, FOUNDER, QUOTES } from "@/data/content";
import { useCanHover } from "@/components/ProgramStack";
import { openWhatsApp, DEMO_MSG, scrollToId } from "@/lib/site";

const CategoryModal = lazy(() => import("@/components/CategoryModal").then((m) => ({ default: m.CategoryModal })));

const HEADLINE = [
  { text: "An Investment" },
  { text: "In Knowledge", gold: true },
  { text: "Pays The Best" },
  { text: "Interest.", gold: true },
];

const HeroTilt = ({ children }) => {
  const ref = useRef(null);
  const canHover = useCanHover();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    if (!canHover) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 900 }} className="relative">
      {children}
    </motion.div>
  );
};

const ProgramsBox = () => (
  <Reveal>
    <div data-testid="hero-programs-box" className="tone-card tone-teal mx-auto mt-14 max-w-2xl rounded-3xl p-6 sm:p-8">
      <p className="eyebrow !text-sm">What We Teach</p>
      <h3 data-testid="programs-heading" className="mt-2 font-display text-4xl font-bold tracking-tight text-forest sm:text-5xl">Programs</h3>
      <ul className="mt-5 divide-y divide-forest/10">
        {PROGRAM_LIST.map((p, i) => (
          <li key={p} data-testid={`program-item-${i}`} className="flex items-center gap-3 py-3 font-display text-xl font-extrabold tracking-tight text-forest sm:text-2xl">
            <span className="tone-badge h-2.5 w-2.5 shrink-0 rounded-full" /> {p}
          </li>
        ))}
      </ul>
    </div>
  </Reveal>
);

const Banner = ({ onEnquire, tight }) => {
  const { photos } = usePhotos("banner");
  return (
    <section data-testid="banner-section" className={`relative overflow-hidden ${tight ? "pt-6" : "pt-28 sm:pt-32"}`}>
      <Coverflow
        photos={photos}
        minSlots={6}
        placeholderLabel="Goyal Academy — banner photo"
        interval={3000}
        height="h-[260px] sm:h-[420px] lg:h-[520px]"
        slide="w-[74%] max-w-[820px] sm:w-[56%]"
        spread={[42, 58]}
        testid="banner-carousel"
        alt="Goyal Academy"
      />
      <div className="relative mx-auto max-w-[1400px] px-3 sm:px-6">
        <div className="mt-10 px-2 sm:mt-12 sm:px-6">
          <h1 data-testid="hero-tagline" className="max-w-4xl font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-forest sm:text-6xl lg:text-7xl">
            {HEADLINE.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span className={`block ${l.gold ? "italic text-terra" : ""}`} initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ delay: 0.25 + i * 0.13, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}>
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="hero-enquire-btn" onClick={onEnquire} className="flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream shadow-lg transition-transform hover:scale-105"><Mail className="h-4 w-4 text-terra" /> Enquire Now</button>
            <button data-testid="hero-demo-btn" onClick={() => openWhatsApp(DEMO_MSG)} className="flex items-center gap-2 rounded-full bg-terra px-6 py-3 text-sm font-bold text-white shadow-lg transition-transform hover:scale-105"><CalendarCheck className="h-4 w-4" /> Book Demo</button>
          </div>
        </div>
      </div>
    </section>
  );
};

const DirectorMessage = () => {
  const { first: founder } = usePhotos("founder");
  return (
    <section id="founder-message" data-testid="director-message" className="texture-grain relative overflow-hidden py-20 lg:py-24">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-terra/10 blur-3xl" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} className="mx-auto w-full max-w-sm lg:sticky lg:top-36">
            <HeroTilt>
              <PhotoFrame photo={founder} priority ratio="aspect-[4/5]" label="Sunny Goyal — Founder & Director" alt="Sunny Goyal — Founder & Director" className="shadow-[0_40px_90px_-40px_rgba(28,35,51,0.45)]" testid="hero-founder-photo" />
            </HeroTilt>
            <div data-testid="hero-mppsc-box" className="glass mx-auto mt-6 w-full rounded-3xl p-5 text-center">
              <p className="font-display text-2xl font-bold text-forest">MPPSC 2019</p>
              <p data-testid="hero-mppsc-badge" className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-charcoal/55">Prelims & Mains Cleared · 2019 Interview Aspirant</p>
              <div className="mx-auto my-4 h-px w-16 bg-terra/40" />
              {FOUNDER.lines.map((l, i) => (
                <p key={l} data-testid={`founder-line-${i}`} className={i === 0 ? "font-display text-lg font-semibold text-forest" : "mt-1 text-sm text-charcoal/75"}>{l}</p>
              ))}
            </div>
          </motion.div>

          <div className="[perspective:1400px]">
            <p className="eyebrow !text-sm">Founder & Director</p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-forest sm:text-5xl lg:text-6xl">Message from Director</h2>
            <p className="mt-2 font-display text-xl italic text-charcoal/70">Sunny Goyal</p>
            <motion.div
              initial={{ rotateX: 10, y: 30, opacity: 0 }}
              whileInView={{ rotateX: 0, y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="glass relative mt-10 rounded-[2rem] p-8 sm:p-12"
            >
              <span className="absolute -top-7 left-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-terra text-white shadow-[0_18px_40px_-14px_rgba(14,90,71,0.8)]"><Quote className="h-6 w-6" /></span>
              {QUOTES.message.split("\n\n").map((para, i) => (
                <p key={i} data-testid={`founder-message-para-${i}`} className="mt-5 font-display text-lg leading-[1.9] text-forest first:mt-2 sm:text-xl">{para}</p>
              ))}
              <p className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-terra">— Sunny Goyal · Founder & Director, Goyal Academy</p>
            </motion.div>
          </div>
        </div>
        <ProgramsBox />
      </div>
    </section>
  );
};

const EnquirySection = () => (
  <section data-testid="enquiry-section" id="enquiry" className="bg-ivory py-24">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
      <div>
        <SectionHeading big align="left" eyebrow="Enquire Now" title="Thirty Seconds Today. A Stronger Tomorrow." sub="Fill the form — it opens WhatsApp with your enquiry pre-typed. Just tap Send, and our team takes it from there." />
        <div className="mt-8 space-y-3">
          {BRAND.phones.map((p) => (
            <a key={p} data-testid={`enquiry-phone-${p}`} href={`tel:+91${p}`} className="flex w-fit items-center gap-3 text-sm font-semibold text-forest transition-colors hover:text-terra">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-terra/40"><Phone className="h-4 w-4 text-terra" /></span>
              +91 {p.slice(0, 5)} {p.slice(5)}
            </a>
          ))}
        </div>
      </div>
      <Reveal><EnquiryForm /></Reveal>
    </div>
  </section>
);

const CategoryGrid = ({ onOpen }) => (
  <section data-testid="category-grid-section" id="explore" className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
    <SectionHeading big eyebrow="At A Glance" title="Syllabus · Courses · Fees · Timings" sub="Tap any card — everything opens right here." />
    <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {CATEGORIES.map((c, i) => (
        <Reveal key={c.id} delay={i * 0.07}>
          <motion.button whileTap={{ scale: 0.96 }} data-testid={`category-tile-${c.id}`} onClick={() => onOpen(c)} className={`tone-card tone-${c.tone} group flex min-h-[210px] w-full flex-col justify-between gap-4 rounded-3xl p-6 text-left transition-all duration-300 hover:-translate-y-2`}>
            <div className="flex items-start justify-between">
              <div className="tone-badge flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <c.icon className="h-8 w-8" strokeWidth={1.6} />
              </div>
              <ArrowUpRight className="tone-text h-5 w-5 shrink-0 transition-all duration-300 group-hover:translate-x-0.5" />
            </div>
            <div>
              <p className="font-display text-2xl font-semibold leading-snug text-forest">{c.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-charcoal/70">{c.desc}</p>
              <span className="tone-text mt-3 inline-block text-[11px] font-bold uppercase tracking-[0.18em]">Tap to open →</span>
            </div>
          </motion.button>
        </Reveal>
      ))}
    </div>
  </section>
);

const DemoBanner = () => (
  <section data-testid="demo-banner" className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
    <Reveal>
      <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-forest p-8 text-cream sm:flex-row sm:items-center sm:p-10">
        <div>
          <p className="eyebrow !text-sm">Experience The Teaching</p>
          <p className="mt-3 font-display text-3xl font-bold sm:text-4xl">Sit in a class before you decide.</p>
        </div>
        <button data-testid="banner-demo-btn" onClick={() => openWhatsApp(DEMO_MSG)} className="flex shrink-0 items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-bold text-forest transition-all duration-300 hover:scale-[1.04] hover:bg-[#0A4636]">
          <MessageCircle className="h-4 w-4" /> Book Demo
        </button>
      </div>
    </Reveal>
  </section>
);

const FacultySection = () => (
  <section data-testid="faculty-section" id="faculty" className="bg-forest py-24 text-cream">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading big light eyebrow="Teachers & Faculty" title="The Mentors Behind the Results" />
      <div className="mt-14"><FacultyList light /></div>
      <div className="mt-12 flex flex-wrap justify-center gap-3">
        <button data-testid="faculty-demo-btn" onClick={() => openWhatsApp(DEMO_MSG)} className="flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-bold text-forest transition-all duration-300 hover:scale-[1.04]"><CalendarCheck className="h-4 w-4" /> Book Demo</button>
        <button data-testid="faculty-enquire-btn" onClick={() => scrollToId("enquiry")} className="flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:scale-[1.04]"><Mail className="h-4 w-4 text-terra" /> Enquire Now</button>
      </div>
    </div>
  </section>
);

export default function Home() {
  const [modal, setModal] = useState(null);
  const [modalReady, setModalReady] = useState(false);
  const openModal = (c) => { setModalReady(true); setModal(c); };
  const announcements = useAnnouncements();
  return (
    <main>
      <AnnouncementBar list={announcements} />
      <Banner onEnquire={() => scrollToId("enquiry")} tight={announcements.length > 0} />
      <DirectorMessage />
      <EnquirySection />
      <CategoryGrid onOpen={openModal} />
      <DemoBanner />
      <ReviewsShowcase />
      <FacultySection />
      <VisionMissionCards />
      <TermsSection />
      {modalReady && <Suspense fallback={null}><CategoryModal category={modal} onClose={() => setModal(null)} /></Suspense>}
    </main>
  );
}
